import { BASE_RECOVERY_ARGUMENT } from "./recoveryAbi";
import { ethers } from "ethers";
import { ACTIVE_EVM_NETWORK_KEY, EVM_CONFIG } from "../config";

export const PRIVATE_SWAP_ROUTE =
  "(address sourcePool,address inputToken,address outputToken,uint256 amountIn,uint256 minimumOut,bytes32 outputNpk,uint64 deadline,uint24 fee,bytes32 salt)";
export function isQualifiedPrivateSwapToken(address: string) {
  return [
    ethers.ZeroAddress.toLowerCase(),
    "0x036cbd53842c5426634e7929541ec2318f3dcf7e",
  ].includes(address.toLowerCase());
}
const LEG =
  "((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) settlement,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes signature)";
export const PRIVATE_SWAP_INTERFACE = new ethers.Interface([
  `function execute(${PRIVATE_SWAP_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT}) returns(bytes32)`,
  `function recover(${PRIVATE_SWAP_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT}) returns(bytes32)`,
  "event PrivateSwapCompleted(bytes32 indexed routeHash,address indexed sourcePool,address indexed outputPool,address inputToken,address outputToken,uint256 amountIn,uint256 amountOut,bytes32 outputCommitment)",
  "event PrivateSwapRecovered(bytes32 indexed routeHash,bytes32 outputCommitment)",
  "event PrivateSwapOutput(bytes32 indexed routeHash,address indexed token,bytes32 indexed npk,uint256 amount,bytes32 tokenHash,bytes32 commitment,uint256 treeIndex)",
  "event LeafInsertedInTree(uint256 indexed treeIndex,bytes32 leaf,uint256 leafIndex)",
]);
export function privateSwapDeployment() {
  // A Base Sepolia deployment: the DogeOS page of the same build has none.
  if (
    !process.env.REACT_APP_PRIVATE_SWAP_COORDINATOR ||
    ACTIVE_EVM_NETWORK_KEY === "dogeos-chikyu"
  )
    return null;
  if (
    EVM_CONFIG.CHAIN_ID !== 84532 ||
    !ethers.isAddress(process.env.REACT_APP_PRIVATE_SWAP_POOL || "") ||
    !ethers.isAddress(
      process.env.REACT_APP_PRIVATE_SWAP_IMPLEMENTATION || "",
    ) ||
    !(Number(process.env.REACT_APP_PRIVATE_SWAP_DEPLOYMENT_BLOCK) > 0)
  )
    throw Error("Swap deployment is not configured");
  return {
    coordinator: ethers.getAddress(
      process.env.REACT_APP_PRIVATE_SWAP_COORDINATOR,
    ),
    pool: ethers.getAddress(process.env.REACT_APP_PRIVATE_SWAP_POOL!),
    implementation: ethers.getAddress(
      process.env.REACT_APP_PRIVATE_SWAP_IMPLEMENTATION!,
    ),
    block: Number(process.env.REACT_APP_PRIVATE_SWAP_DEPLOYMENT_BLOCK),
  };
}
export function privateSwapRouteHash(route: any) {
  const deployment = privateSwapDeployment();
  if (!deployment) throw Error("Swaps are not configured");
  return ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      ["uint256", "address", PRIVATE_SWAP_ROUTE],
      [84532, deployment.coordinator, route],
    ),
  );
}
export function privateSwapEscrow(route: any) {
  const deployment = privateSwapDeployment();
  if (!deployment) throw Error("Swaps are not configured");
  const code =
    "0x3d602d80600a3d3981f3363d3d373d3d3d363d73" +
    deployment.implementation.slice(2).toLowerCase() +
    "5af43d82803e903d91602b57fd5bf3";
  return ethers.getCreate2Address(
    deployment.coordinator,
    privateSwapRouteHash(route),
    ethers.keccak256(code),
  );
}
export function decodePrivateSwap(data: string) {
  try {
    const decoded = PRIVATE_SWAP_INTERFACE.parseTransaction({ data });
    if (
      !decoded ||
      !["execute", "recover"].includes(decoded.name) ||
      PRIVATE_SWAP_INTERFACE.encodeFunctionData(
        decoded.fragment,
        decoded.args,
      ).toLowerCase() !== data.toLowerCase()
    )
      return null;
    const { route, leg } = decoded.args;
    if (
      ethers.getAddress(leg.settlement.recipient) !==
        privateSwapEscrow(route) ||
      leg.settlement.token !== route.inputToken ||
      leg.settlement.publicAmount !== route.amountIn ||
      Number(leg.quote.kind) !== 1
    )
      return null;
    return { route, leg, recovery: decoded.name === "recover" };
  } catch {
    return null;
  }
}
